import type {
	ITokenService,
	IAuthenticationService,
} from "@/application/ports/services";
import type { ITokenManager } from "@/application/ports/services/ITokenManager";
import type { TokenPayload } from "@/domain";
import type { ILogger } from "@/application/ports/ILogger";

const ROLES = {
	INVESTOR: "investor",
} as const;

/**
 * Manages JWT token lifecycle including validation, refresh, and payload extraction.
 */
export class TokenManager implements ITokenManager {
	private readonly tokenService: ITokenService;
	private readonly logger: ILogger;
	private authService: IAuthenticationService | null = null;
	private refreshPromise: Promise<string | null> | null = null;

	constructor(
		tokenService: ITokenService,
		authService?: IAuthenticationService,
		logger?: ILogger,
	) {
		this.tokenService = tokenService;
		this.authService = authService ?? null;
		this.logger = logger ?? { debug() {}, info() {}, warn() {}, error() {} };
	}

	/** Returns the current token if valid, or null. Clears expired tokens. */
	private getValidToken(): string | null {
		const token = this.tokenService.getToken();
		if (!token) return null;

		if (this.tokenService.isTokenExpired(token)) {
			this.tokenService.removeToken();
			return null;
		}

		return token;
	}

	/** Clears all stored tokens. */
	private clearTokens(): void {
		this.tokenService.removeToken();
		this.tokenService.removeRefreshToken();
	}

	/** Parses and returns the JWT payload, or null on failure. */
	private getPayload(): TokenPayload | null {
		const token = this.getValidToken();
		if (!token) return null;

		try {
			return JSON.parse(atob(token.split(".")[1])) as TokenPayload;
		} catch (error) {
			this.logger.error("Failed to parse JWT payload:", error);
			return null;
		}
	}

	/** Returns the organization roles from the token. */
	private getOrgRoles(): string[] {
		return this.getPayload()?.org_roles ?? [];
	}

	/** Checks if the user has any of the specified roles. */
	private hasAnyRole(requiredRoles: string[]): boolean {
		const userRoles = this.getOrgRoles();
		return requiredRoles.some((role) => userRoles.includes(role));
	}

	/** Checks if the user is a platform administrator. */
	isPlatformAdmin(): boolean {
		return this.getPayload()?.is_platform_admin === true;
	}

	/** Checks if the user has the investor role. */
	isInvestor(): boolean {
		return this.hasAnyRole([ROLES.INVESTOR]);
	}

	/** Checks if the user is exclusively an investor (no other roles). */
	isInvestorOnly(): boolean {
		const roles = this.getOrgRoles();
		return roles.length === 1 && roles[0] === ROLES.INVESTOR;
	}

	/** Returns a valid token, refreshing if necessary. */
	async ensureValidToken(): Promise<string> {
		const currentToken = this.tokenService.getToken();
		if (!currentToken) throw new Error("No token available");

		if (!this.tokenService.isTokenExpired(currentToken)) return currentToken;

		const refreshedToken = await this.refreshToken();
		if (!refreshedToken) throw new Error("Token refresh failed");

		return refreshedToken;
	}

	/** Forces a token refresh regardless of expiry status. */
	async forceRefresh(): Promise<string | null> {
		return this.refreshToken();
	}

	/** Refreshes the token with request deduplication. */
	private async refreshToken(): Promise<string | null> {
		if (this.refreshPromise) return this.refreshPromise;

		this.refreshPromise = this.performRefresh();

		try {
			return await this.refreshPromise;
		} finally {
			this.refreshPromise = null;
		}
	}

	/** Executes the actual token refresh request. */
	private async performRefresh(): Promise<string | null> {
		if (!this.authService) {
			this.logger.warn("No auth service available for token refresh");
			this.clearTokens();
			return null;
		}

		try {
			const result = await this.authService.refreshToken();

			if (result.success && result.token) {
				this.tokenService.setToken(result.token);
				if (result.refreshToken) {
					this.tokenService.setRefreshToken(result.refreshToken);
				}
				return result.token;
			}

			this.logger.warn("Token refresh failed:", result.error);

			// Only clear tokens for definitive auth failures, not transient errors
			if (result.errorCode !== "transient_error") {
				this.clearTokens();
			}
			return null;
		} catch (error) {
			this.logger.error("Token refresh error:", error);
			this.clearTokens();
			return null;
		}
	}
}
